import { useState, useEffect } from 'react'

interface DeviceInfo {
  isMobile: boolean
  isSmallMobile: boolean
  isTablet: boolean
  isDesktop: boolean
  isTouch: boolean
  screenWidth: number
  screenHeight: number
}

/**
 * Hook to detect device type and screen size
 * Used for responsive rendering decisions
 */
export function useDeviceDetect(): DeviceInfo {
  // Default to desktop in SSR
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>({
    isMobile: false,
    isSmallMobile: false,
    isTablet: false,
    isDesktop: true,
    isTouch: false,
    screenWidth: typeof window !== 'undefined' ? window.innerWidth : 1200,
    screenHeight: typeof window !== 'undefined' ? window.innerHeight : 800,
  })

  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return

    // Function to update device info
    const updateDeviceInfo = () => {
      const width = window.innerWidth
      const height = window.innerHeight

      // Detect touch device
      const isTouch =
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        (navigator as any).msMaxTouchPoints > 0

      // Set device type based on screen width
      setDeviceInfo({
        isMobile: width < 768,
        isSmallMobile: width < 480,
        isTablet: width >= 768 && width < 1024,
        isDesktop: width >= 1024,
        isTouch,
        screenWidth: width,
        screenHeight: height,
      })
    }

    // Initial check
    updateDeviceInfo()

    // Add resize listener
    window.addEventListener('resize', updateDeviceInfo)

    // Cleanup
    return () => window.removeEventListener('resize', updateDeviceInfo)
  }, [])

  return deviceInfo
}

/**
 * Hook to detect orientation changes
 * @returns Current orientation (landscape or portrait)
 */
export function useOrientation(): 'portrait' | 'landscape' {
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>(
    typeof window !== 'undefined' && window.innerHeight > window.innerWidth
      ? 'portrait'
      : 'landscape'
  )

  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return

    // Function to update orientation
    const updateOrientation = () => {
      if (window.innerHeight > window.innerWidth) {
        setOrientation('portrait')
      } else {
        setOrientation('landscape')
      }
    }

    // Initial check
    updateOrientation()

    // Add resize and orientation change listeners
    window.addEventListener('resize', updateOrientation)
    window.addEventListener('orientationchange', updateOrientation)

    // Cleanup
    return () => {
      window.removeEventListener('resize', updateOrientation)
      window.removeEventListener('orientationchange', updateOrientation)
    }
  }, [])

  return orientation
}

export default useDeviceDetect
